/**
 * Custom Font Loader Utility
 * Handles loading custom fonts for public pages
 */

import { getDatabase, ref, get } from 'firebase/database';

/**
 * Load custom fonts for a specific user (with public fallback)
 */
export const loadUserCustomFonts = async (userId, spaceId = null) => {
    if (!userId) return [];
    
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    console.log(`📱 loadUserCustomFonts called for userId: ${userId}, spaceId: ${spaceId} (Mobile: ${isMobile})`);
    
    try {
        const db = getDatabase();
        
        // 🔥 Strategy 1: Try public space fonts first (no auth required)
        if (spaceId) {
            try {
                const publicSpaceRef = ref(db, `/publicSpaces/${spaceId}/customFonts`);
                const publicSnapshot = await get(publicSpaceRef);
                
                if (publicSnapshot.exists()) {
                    const customFontsData = publicSnapshot.val();
                    const customFontsArray = Object.entries(customFontsData).map(([key, value]) => ({
                        id: key,
                        ...value
                    }));
                    
                    console.log(`✅ Found ${customFontsArray.length} custom fonts in public space ${spaceId}:`, customFontsArray.map(f => f.name));
                    
                    // ✅ PARALLEL Loading: Alle Fonts gleichzeitig laden
                    await Promise.all(
                        customFontsArray.map(font => {
                            console.log(`📱 Loading public font: ${font.name}`);
                            return injectCustomFont(font);
                        })
                    );
                    
                    console.log(`✅ Loaded ${customFontsArray.length} custom fonts from public space (Mobile: ${isMobile})`);
                    return customFontsArray;
                }
            } catch (publicError) {
                console.log(`📱 No custom fonts in public space ${spaceId}, trying user fonts...`);
            }
        }
        
        // 🔥 Strategy 2: Try user fonts (requires auth)
        try {
            const customFontsRef = ref(db, `/users/${userId}/customFonts`);
            const snapshot = await get(customFontsRef);
            
            if (snapshot.exists()) {
                const customFontsData = snapshot.val();
                const customFontsArray = Object.entries(customFontsData).map(([key, value]) => ({
                    id: key,
                    ...value
                }));
                
                console.log(`📱 Found ${customFontsArray.length} custom fonts for user ${userId}:`, customFontsArray.map(f => f.name));
                
                // ✅ PARALLEL Loading: Alle Fonts gleichzeitig laden
                await Promise.all(
                    customFontsArray.map(font => {
                        console.log(`📱 Loading font: ${font.name}`);
                        return injectCustomFont(font);
                    })
                );
                
                console.log(`✅ Loaded ${customFontsArray.length} custom fonts for user ${userId} (Mobile: ${isMobile})`);
                return customFontsArray;
            } else {
                console.log(`📱 No custom fonts found for user ${userId}`);
            }
        } catch (userFontsError) {
            // 🔥 CRITICAL FIX: Handle permission denied errors gracefully
            if (userFontsError.code === 'PERMISSION_DENIED' || userFontsError.message?.includes('Permission denied')) {
                console.log(`⚠️ Permission denied loading user fonts for ${userId} (user not authenticated) - this is normal for public pages`);
            } else {
                console.error(`❌ Error loading user custom fonts for ${userId}:`, userFontsError);
            }
        }
    } catch (error) {
        console.error(`❌ Error in loadUserCustomFonts for ${userId}:`, error);
    }
    
    return [];
};

/**
 * Load custom fonts from page metadata
 */
export const loadPageCustomFonts = async (pageData) => {
    if (!pageData?.customFonts || !Array.isArray(pageData.customFonts)) {
        console.log(`📱 No page custom fonts found in pageData`);
        return [];
    }
    
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    console.log(`📱 loadPageCustomFonts called with ${pageData.customFonts.length} fonts (Mobile: ${isMobile})`);
    
    try {
        // ✅ PARALLEL Loading: Alle Page Fonts gleichzeitig laden
        await Promise.all(
            pageData.customFonts.map(font => {
                console.log(`📱 Loading page font: ${font.name}`);
                return injectCustomFont(font);
            })
        );
        
        console.log(`✅ Loaded ${pageData.customFonts.length} custom fonts from page metadata (Mobile: ${isMobile})`);
        return pageData.customFonts;
    } catch (error) {
        console.error(`❌ Error loading page custom fonts:`, error);
    }
    
    return [];
};

/**
 * Inject custom font CSS into document head (supports multi-weight)
 */
export const injectCustomFont = async (fontData) => {
    const { name, weights, fileUrl, format = 'truetype' } = fontData;
    
    // ✅ CRITICAL: Check if CSS is already injected (prevents duplicates + flicker)
    const existingStyle = document.querySelector(`[data-custom-font="${name}"]`);
    if (existingStyle) {
        console.log(`📱 Custom font ${name} CSS already injected - skipping to prevent flicker`);
        return;
    }
    
    // ✅ REMOVED: Unnötiger Log (war irreführend)
    // Log if font is available locally vs. needs to be loaded
    // const isLocallyAvailable = document.fonts.check(`16px "${name}"`);
    // console.log(`📱 Font ${name} locally available: ${isLocallyAvailable} - Will inject custom version anyway`);
    
    try {
        let fontCSS = '';
        
        // Handle multi-weight font family
        if (weights && Array.isArray(weights)) {
            console.log(`📱 Injecting font family ${name} with ${weights.length} weights (Mobile-optimized)`);
            
            for (const weight of weights) {
                fontCSS += `
                    @font-face {
                        font-family: '${name}';
                        src: url('${weight.fileUrl}') format('${weight.format}');
                        font-weight: ${weight.weightValue};
                        font-style: normal;
                        font-display: block;
                        unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
                    }
                `;
            }
        } else {
            // Handle single weight font (legacy)
            console.log(`📱 Injecting single-weight font family ${name} (Legacy mode)`);
            fontCSS = `
                @font-face {
                    font-family: '${name}';
                    src: url('${fileUrl}') format('${format}');
                    font-weight: normal;
                    font-style: normal;
                    font-display: block;
                    unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
                }
            `;
        }
        
        // Inject CSS
        const styleElement = document.createElement('style');
        styleElement.setAttribute('data-custom-font', name);
        styleElement.textContent = fontCSS;
        document.head.appendChild(styleElement);
        
        console.log(`📱 CSS injected for font ${name}, now attempting to load...`);
        
        // Force font loading regardless of local availability
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        const loadTimeout = isMobile ? 3000 : 5000;
        
        try {
            // Force load the custom font by trying different weights
            const loadPromises = [];
            
            if (weights && Array.isArray(weights)) {
                // Load all weights
                weights.forEach(weight => {
                    loadPromises.push(document.fonts.load(`${weight.weightValue} 16px "${name}"`));
                });
            } else {
                // Load normal weight
                loadPromises.push(document.fonts.load(`16px "${name}"`));
            }
            
            console.log(`📱 Loading ${loadPromises.length} font variants for ${name}...`);
            
            // Wait for all font variants to load with timeout
            await Promise.race([
                Promise.all(loadPromises),
                new Promise((_, reject) => 
                    setTimeout(() => reject(new Error('Font load timeout')), loadTimeout)
                )
            ]);
            
            console.log(`✅ Custom font family loaded: ${name} (${weights?.length || 1} weights)`);
            
            // ✅ REMOVED: Forced reflow eliminiert (font-display: swap handled das bereits!)
            // Kein Reflow = Kein Flackern
            
        } catch (loadError) {
            console.warn(`⚠️ Font ${name} load timeout or failed, but CSS injected (will fallback gracefully):`, loadError.message);
            
            // ✅ REMOVED: Auch beim Fallback kein Reflow mehr
        }
        
    } catch (error) {
        console.error(`❌ Failed to inject custom font ${name}:`, error);
        throw error;
    }
};

/**
 * Get font format from filename
 */
export const detectFontFormat = (fileName) => {
    const extension = fileName.toLowerCase().split('.').pop();
    const formatMap = {
        'ttf': 'truetype',
        'otf': 'opentype',
        'woff': 'woff',
        'woff2': 'woff2'
    };
    return formatMap[extension] || 'truetype';
};

/**
 * Mobile-optimized font preloader
 * Preloads fonts with link rel="preload" for faster loading on mobile
 */
export const preloadFontsForMobile = (fontArray) => {
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    if (!isMobile || !fontArray || fontArray.length === 0) {
        return;
    }
    
    console.log(`📱 Preloading ${fontArray.length} fonts for mobile optimization`);
    
    fontArray.forEach(font => {
        if (font.weights && Array.isArray(font.weights)) {
            // Preload multi-weight fonts
            font.weights.forEach(weight => {
                const link = document.createElement('link');
                link.rel = 'preload';
                link.as = 'font';
                link.type = `font/${weight.format || 'truetype'}`;
                link.href = weight.fileUrl;
                link.crossOrigin = 'anonymous';
                document.head.appendChild(link);
                console.log(`📱 Preloaded font weight: ${font.name} ${weight.weightValue}`);
            });
        } else if (font.fileUrl) {
            // Preload single weight font
            const link = document.createElement('link');
            link.rel = 'preload';
            link.as = 'font';
            link.type = `font/${font.format || 'truetype'}`;
            link.href = font.fileUrl;
            link.crossOrigin = 'anonymous';
            document.head.appendChild(link);
            console.log(`📱 Preloaded font: ${font.name}`);
        }
    });
};

/**
 * Sanitize font name for CSS usage
 */
export const sanitizeFontName = (name) => {
    return name
        .replace(/[^a-zA-Z0-9\s-]/g, '') // Remove special characters
        .replace(/\s+/g, ' ')           // Normalize spaces
        .trim();
};
